import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '@/context/language-context';
import { useAuth } from '@/context/auth';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { Factor } from '@supabase/supabase-js';
import { 
  KeyRound, Shield, AlertTriangle, UserX, Lock, 
  Smartphone, Check, Phone, Plus, Trash2, Eye, EyeOff, AlertCircle, Loader2
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';

interface BlockedUser {
  id: string; // ID de l'utilisateur bloqué (blocked_id)
  relationshipId: string; // ID de la relation de blocage
  name: string;
  blockedDate: string;
  avatar?: string | null;
}

interface EmergencyContact {
  id: string;
  user_id: string; // Ajouter user_id pour la relation BDD
  name: string;
  phone: string;
  email: string | null; // Email peut être null
  is_main: boolean;
  created_at: string; // Ajouter created_at si on veut l'afficher
  updated_at: string; // Ajouter updated_at si on veut l'afficher
}

// Supprimer les données mockées
/*
const mockedBlockedUsers: BlockedUser[] = [
  {
    id: '1',
    relationshipId: 'rel1', // Ajouter un ID de relation fictif
    name: 'John Doe',
    blockedDate: '2023-06-15',
  },
  {
    id: '2',
    relationshipId: 'rel2', // Ajouter un ID de relation fictif
    name: 'Alice Smith',
    blockedDate: '2023-07-02',
  },
];
*/

const EscortSecurity: React.FC = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  
  // États pour 2FA
  const [mfaFactors, setMfaFactors] = useState<Factor[]>([]);
  const [isEnrollingMfa, setIsEnrollingMfa] = useState(false);
  const [isVerifyingMfa, setIsVerifyingMfa] = useState(false);
  const [isDisablingMfa, setIsDisablingMfa] = useState(false);
  const [enrollmentFactorId, setEnrollmentFactorId] = useState<string | null>(null);
  const [enrollmentQrCodeSvg, setEnrollmentQrCodeSvg] = useState<string | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [showAddContactForm, setShowAddContactForm] = useState(false);
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [blockedUsers, setBlockedUsers] = useState<BlockedUser[]>([]);
  const [emergencyContacts, setEmergencyContacts] = useState<EmergencyContact[]>([]);
  const [isLoadingBlockedUsers, setIsLoadingBlockedUsers] = useState(true);
  const [isLoadingEmergencyContacts, setIsLoadingEmergencyContacts] = useState(true);
  const [isSavingContact, setIsSavingContact] = useState(false);
  const [isDeletingContact, setIsDeletingContact] = useState<string | null>(null); // Stocker l'ID en cours de suppression
  const [isSettingMain, setIsSettingMain] = useState<string | null>(null); // Stocker l'ID en cours de définition
  
  // Charger les facteurs MFA, utilisateurs bloqués et contacts d'urgence au montage
  const loadMfaFactors = useCallback(async () => {
    const { data, error } = await supabase.auth.mfa.listFactors();
    if (data) {
      // Trouver le premier facteur TOTP vérifié
      const totpFactor = data.totp.find(factor => factor.status === 'verified');
      setMfaFactors(data.totp); // Stocker tous les facteurs TOTP
      // Mettre à jour l'état is2FAEnabled basé sur l'existence d'un facteur vérifié
      // Note: L'état is2FAEnabled local n'est plus vraiment nécessaire, on peut se baser sur mfaFactors
    } else {
      console.error("Error loading MFA factors:", error);
      setMfaFactors([]);
    }
  }, [supabase.auth.mfa]);

  // Helper pour vérifier si la 2FA est activée (au moins un facteur TOTP vérifié)
  const isTotpEnabled = () => {
    return mfaFactors.some(factor => factor.status === 'verified');
  }

  // Charger les utilisateurs bloqués
  const loadBlockedUsers = useCallback(async () => {
    if (!user?.id) return;
    setIsLoadingBlockedUsers(true);
    try {
      const { data, error } = await supabase
        .from('blocked_relationships')
        .select(`
          id, 
          created_at, 
          blockedProfile:blocked_id (id, display_name, avatar_url)
        `)
        .eq('blocker_id', user.id);

      if (error) throw error;

      const formattedBlockedUsers: BlockedUser[] = (data || []).map(item => ({
        id: item.blockedProfile.id,
        relationshipId: item.id, // ID de la ligne dans blocked_relationships
        name: item.blockedProfile.display_name || t('common.unknown_user'),
        blockedDate: item.created_at,
        avatar: item.blockedProfile.avatar_url
      }));
      
      setBlockedUsers(formattedBlockedUsers);
    } catch (error) {
      console.error("Error loading blocked users:", error);
      toast({ title: t('common.error'), description: t('escort:security.error_loading_blocked') + `: ${(error instanceof Error && error.message) || 'Unknown error'}`, variant: 'destructive' });
    } finally {
      setIsLoadingBlockedUsers(false);
    }
  }, [supabase, user?.id, t, toast]);

  // Charger les contacts d'urgence
  const loadEmergencyContacts = useCallback(async () => {
    if (!user?.id) return;
    setIsLoadingEmergencyContacts(true);
    try {
      const { data, error } = await supabase
        .from('emergency_contacts')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at'); // Trier par date de création ou is_main?

      if (error) throw error;
      
      setEmergencyContacts(data || []);
    } catch (error) {
      console.error("Error loading emergency contacts:", error);
      toast({ title: t('common.error'), description: t('escort:security.error_loading_contacts') + `: ${(error instanceof Error && error.message) || 'Unknown error'}`, variant: 'destructive' });
    } finally {
      setIsLoadingEmergencyContacts(false);
    }
  }, [supabase, user?.id, t, toast]);

  useEffect(() => {
    loadMfaFactors();
    loadBlockedUsers();
    loadEmergencyContacts();
  }, [loadMfaFactors, loadBlockedUsers, loadEmergencyContacts]);

  const handleChangePassword = async () => {
    if (!newPassword) {
      toast({ title: t('common.error'), description: t('escort:security.error_new_password_required'), variant: 'destructive' });
      return;
    }
    if (newPassword.length < 6) {
      toast({ title: t('common.error'), description: t('escort:security.error_password_too_short'), variant: 'destructive' });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({ title: t('common.error'), description: t('escort:security.error_passwords_mismatch'), variant: 'destructive' });
      return;
    }

    setIsChangingPassword(true);
    try {
      const { error } = await supabase.auth.updateUser({ 
        password: newPassword 
      });

      if (error) {
        console.error("Error updating password:", error);
        let errorMessage = error.message;
        if (error.message.includes("Password should be at least 6 characters")) {
          errorMessage = t('escort:security.error_password_too_short');
        }
        
        toast({ 
          title: t('common.error'), 
          description: errorMessage, 
          variant: 'destructive' 
        });
      } else {
        toast({ 
          title: t('common.success'), 
          description: t('escort:security.password_change_success') 
        });
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      }
    } catch (error) {
      console.error("Unexpected error updating password:", error);
      toast({ 
        title: t('common.error'), 
        description: (error instanceof Error && error.message) || t('common.unknown_error_occurred'), 
        variant: 'destructive' 
      });
    } finally {
      setIsChangingPassword(false);
    }
  };
  
  // Fonction pour démarrer l'enrôlement 2FA
  const handleEnrollMfa = async () => {
    setIsEnrollingMfa(true);
    setEnrollmentQrCodeSvg(null);
    setEnrollmentFactorId(null);
    setVerificationCode('');

    try {
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        // issuer: 'VotreAppNom', // Optionnel: Nom de l'application dans l'app d'authentification
        // friendlyName: 'TOTP Principal' // Optionnel: Nom convivial
      });

      if (error) throw error;

      if (data) {
        setEnrollmentFactorId(data.id);
        // Utiliser le QR code SVG fourni par Supabase
        setEnrollmentQrCodeSvg(data.totp.qr_code);
      }
    } catch (error) {
      console.error("Error enrolling MFA factor:", error);
      toast({ title: t('common.error'), description: t('escort:security.error_enrolling_2fa') + `: ${(error instanceof Error && error.message) || 'Unknown error'}`, variant: 'destructive' });
    } finally {
      setIsEnrollingMfa(false);
    }
  };

  // Fonction pour vérifier le code et activer le facteur
  const handleVerifyMfa = async () => {
    if (!enrollmentFactorId || !verificationCode) return;
    setIsVerifyingMfa(true);

    try {
      // 1. Créer un challenge
      const challenge = await supabase.auth.mfa.challenge({ factorId: enrollmentFactorId });
      if (challenge.error) throw challenge.error;

      const challengeId = challenge.data.id;

      // 2. Vérifier le challenge avec le code fourni
      const { error: verifyError } = await supabase.auth.mfa.verify({ 
        factorId: enrollmentFactorId, 
        challengeId: challengeId, 
        code: verificationCode 
      });

      if (verifyError) throw verifyError;

      // Succès!
      toast({ title: t('common.success'), description: t('escort:security.2fa_enabled_success') });
      setEnrollmentFactorId(null); // Réinitialiser le processus d'enrôlement
      setEnrollmentQrCodeSvg(null);
      setVerificationCode('');
      loadMfaFactors(); // Recharger les facteurs pour mettre à jour l'état

    } catch (error) {
      console.error("Error verifying MFA code:", error);
      let errorMessage = t('escort:security.error_verifying_2fa');
      if (error instanceof Error && error.message.includes('Invalid TOTP code')) {
        errorMessage = t('escort:security.error_invalid_code');
      }
      toast({ title: t('common.error'), description: errorMessage + `: ${(error instanceof Error && error.message) || 'Unknown error'}`, variant: 'destructive' });
    } finally {
      setIsVerifyingMfa(false);
    }
  };
  
  // Fonction pour désactiver la 2FA (supprimer le premier facteur TOTP vérifié)
  const handleDisableMfa = async () => {
    const verifiedFactor = mfaFactors.find(factor => factor.status === 'verified');
    if (!verifiedFactor) return;

    setIsDisablingMfa(true);
    try {
      const { error } = await supabase.auth.mfa.unenroll({ factorId: verifiedFactor.id });
      if (error) throw error;

      toast({ title: t('common.success'), description: t('escort:security.2fa_disabled_success') });
      loadMfaFactors(); // Recharger les facteurs
    } catch (error) {
      console.error("Error unenrolling MFA factor:", error);
      toast({ title: t('common.error'), description: t('escort:security.error_disabling_2fa') + `: ${(error instanceof Error && error.message) || 'Unknown error'}`, variant: 'destructive' });
    } finally {
      setIsDisablingMfa(false);
    }
  }
  
  // Fonction pour débloquer un utilisateur (utilise l'ID de la relation)
  const handleUnblockUser = async (relationshipId: string) => {
    if (!user?.id) return;

    // Confirmation
    if (!window.confirm(t('escort:security.confirm_unblock'))) {
      return;
    }

    try {
      const { error } = await supabase
        .from('blocked_relationships')
        .delete()
        .eq('id', relationshipId)
        // Double sécurité: s'assurer qu'on ne supprime que si l'utilisateur actuel est le bloqueur
        .eq('blocker_id', user.id); 

      if (error) throw error;

      toast({ title: t('common.success'), description: t('escort:security.user_unblocked_success') });
      loadBlockedUsers(); // Recharger la liste
    } catch (error) {
      console.error("Error unblocking user:", error);
      toast({ title: t('common.error'), description: t('escort:security.error_unblocking') + `: ${(error instanceof Error && error.message) || 'Unknown error'}`, variant: 'destructive' });
    }
  };
  
  // Ajouter un contact d'urgence
  const handleAddContact = async () => {
    if (!user?.id || !contactName || !contactPhone) {
      toast({
        title: t('common.error'),
        description: t('escort:security.error_contact_fields_required'),
        variant: "destructive",
      });
      return;
    }

    setIsSavingContact(true);
    try {
       // Vérifier s'il y a déjà un contact principal
       const alreadyHasMain = emergencyContacts.some(c => c.is_main);

       const { data, error } = await supabase
            .from('emergency_contacts')
            .insert({
                user_id: user.id,
                name: contactName,
                phone: contactPhone,
                email: contactEmail || null,
                // Le premier contact ajouté devient le principal si aucun n'existe
                is_main: !alreadyHasMain 
            })
            .select()
            .single(); // Récupérer le contact inséré

       if (error) throw error;

       // Si le trigger `ensure_single_main_contact` n'est pas utilisé ou fiable,
       // on pourrait devoir mettre à jour les autres contacts ici si le nouveau est principal.

        toast({ title: t('common.success'), description: t('escort:security.contact_added_success') });
        loadEmergencyContacts(); // Recharger la liste
        
        // Réinitialiser le formulaire
        setShowAddContactForm(false);
        setContactName('');
        setContactPhone('');
        setContactEmail('');

    } catch (error) {
        console.error("Error adding emergency contact:", error);
        toast({ title: t('common.error'), description: t('escort:security.error_adding_contact') + `: ${(error instanceof Error && error.message) || 'Unknown error'}`, variant: 'destructive' });
    } finally {
       setIsSavingContact(false);
    }
  };
  
  // Supprimer un contact d'urgence
  const handleDeleteContact = async (id: string) => {
     // Confirmation
     if (!window.confirm(t('common:confirm_delete_item'))) {
       return;
     }
     setIsDeletingContact(id);
     try {
         const contactToDelete = emergencyContacts.find(c => c.id === id);

         const { error } = await supabase
             .from('emergency_contacts')
             .delete()
             .eq('id', id);

         if (error) throw error;

         // Si on supprime le contact principal, en choisir un autre s'il en reste?
         // Le trigger devrait gérer la non-existence d'un principal, mais on peut forcer ici.
         if (contactToDelete?.is_main && emergencyContacts.length > 1) {
             const nextContact = emergencyContacts.find(c => c.id !== id);
             if (nextContact) {
                 await handleSetMainContact(nextContact.id, false); // false pour ne pas re-toaster succès
             }
         }

         toast({ title: t('common.success'), description: t('escort:security.contact_deleted_success') });
         loadEmergencyContacts(); // Recharger la liste

     } catch (error) {
         console.error("Error deleting emergency contact:", error);
         toast({ title: t('common.error'), description: t('escort:security.error_deleting_contact') + `: ${(error instanceof Error && error.message) || 'Unknown error'}`, variant: 'destructive' });
     } finally {
        setIsDeletingContact(null);
     }
  };
  
  // Définir un contact comme principal
  const handleSetMainContact = async (id: string, showToast = true) => {
      setIsSettingMain(id);
      try {
         // La logique de mise à jour des autres est gérée par le trigger
         // On met juste à jour celui-ci pour être principal
         const { error } = await supabase
             .from('emergency_contacts')
             .update({ is_main: true })
             .eq('id', id);

          if (error) throw error;

          if(showToast) {
              toast({ title: t('common.success'), description: t('escort:security.main_contact_set_success') });
          }
          loadEmergencyContacts(); // Recharger la liste

      } catch (error) {
          console.error("Error setting main emergency contact:", error);
          if(showToast) {
              toast({ title: t('common.error'), description: t('escort:security.error_setting_main_contact') + `: ${(error instanceof Error && error.message) || 'Unknown error'}`, variant: 'destructive' });
          }
      } finally {
          setIsSettingMain(null);
      }
  };
  
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold mb-2">{t('escort:security.title')}</h1>
        <p className="text-muted-foreground">{t('escort:security.description')}</p>
      </div>

      <Tabs defaultValue="password" className="w-full">
        <TabsList className="grid grid-cols-2 md:grid-cols-4 mb-6">
          <TabsTrigger value="password" className="flex items-center gap-2">
            <KeyRound className="h-4 w-4" />
            <span className="hidden md:inline">{t('escort:security.change_password')}</span>
            <span className="md:hidden">Mot de passe</span>
          </TabsTrigger>
          <TabsTrigger value="2fa" className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            <span className="hidden md:inline">{t('escort:security.two_factor_auth')}</span>
            <span className="md:hidden">2FA</span>
          </TabsTrigger>
          <TabsTrigger value="blocked" className="flex items-center gap-2">
            <UserX className="h-4 w-4" />
            <span className="hidden md:inline">{t('escort:security.blocked_users')}</span>
            <span className="md:hidden">Bloqués</span>
          </TabsTrigger>
          <TabsTrigger value="emergency" className="flex items-center gap-2">
            <Phone className="h-4 w-4" />
            <span className="hidden md:inline">{t('escort:security.panic_button')}</span>
            <span className="md:hidden">Urgence</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="password">
          <Card>
            <CardHeader>
              <CardTitle>{t('escort:security.change_password')}</CardTitle>
              <CardDescription>
                Modifiez votre mot de passe pour maintenir la sécurité de votre compte
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1">
                <Label htmlFor="current-password">{t('escort:security.current_password')}</Label>
                <div className="relative">
                  <Input
                    id="current-password"
                    type={showCurrentPassword ? "text" : "password"}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    disabled={isChangingPassword}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    type="button"
                    className="absolute right-0 top-0 h-full px-3"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    aria-label={showCurrentPassword ? "Hide password" : "Show password"}
                  >
                    {showCurrentPassword ? (
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Eye className="h-4 w-4 text-muted-foreground" />
                    )}
                  </Button>
                </div>
              </div>
              
              <div className="space-y-1">
                <Label htmlFor="new-password">{t('escort:security.new_password')} *</Label>
                <div className="relative">
                  <Input
                    id="new-password"
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    disabled={isChangingPassword}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    type="button"
                    className="absolute right-0 top-0 h-full px-3"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    aria-label={showNewPassword ? "Hide password" : "Show password"}
                  >
                    {showNewPassword ? (
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Eye className="h-4 w-4 text-muted-foreground" />
                    )}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">{t('escort:security.password_length_hint')}</p>
              </div>
              
              <div className="space-y-1">
                <Label htmlFor="confirm-password">{t('escort:security.confirm_new_password')} *</Label>
                <div className="relative">
                  <Input
                    id="confirm-password"
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={isChangingPassword}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    type="button"
                    className="absolute right-0 top-0 h-full px-3"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Eye className="h-4 w-4 text-muted-foreground" />
                    )}
                  </Button>
                </div>
              </div>
              
              {newPassword && confirmPassword && newPassword !== confirmPassword && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Les mots de passe ne correspondent pas
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
            <CardFooter>
              <Button onClick={handleChangePassword} disabled={isChangingPassword}>
                {isChangingPassword 
                    ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t('common:saving')}</> 
                    : t('escort:security.update_password_button')}
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>

        <TabsContent value="2fa">
          <Card>
            <CardHeader>
              <CardTitle>{t('escort:security.two_factor_auth')}</CardTitle>
              <CardDescription>
                {t('escort:security.2fa_description')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
             {/* Affichage basé sur l'état d'activation réel via isTotpEnabled() */}
             {!isTotpEnabled() && !enrollmentFactorId && (
                 // Utiliser handleEnrollMfa pour démarrer l'activation
                 <Button onClick={handleEnrollMfa} disabled={isEnrollingMfa}>
                     {isEnrollingMfa 
                         ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t('common:loading')}</>
                         : t('escort:security.enable_2fa_button')}
                 </Button>
             )}

             {/* Étape 1: Afficher QR Code et champ de vérification (utilise enrollmentFactorId et enrollmentQrCodeSvg) */}
             {enrollmentFactorId && enrollmentQrCodeSvg && (
                 <div className="space-y-4">
                     <p>{t('escort:security.scan_qr_code')}</p>
                     <div dangerouslySetInnerHTML={{ __html: enrollmentQrCodeSvg }} className="p-4 bg-white inline-block rounded-lg shadow"/>
                     
                     <div className="space-y-1 max-w-xs">
                        <Label htmlFor="verification-code">{t('escort:security.verification_code')}</Label>
                        <Input 
                           id="verification-code"
                           value={verificationCode}
                           onChange={(e) => setVerificationCode(e.target.value)}
                           placeholder="123456"
                           maxLength={6}
                           disabled={isVerifyingMfa}
                        />
                     </div>
                     <div className="flex gap-2">
                        {/* Utiliser handleVerifyMfa */}
                        <Button onClick={handleVerifyMfa} disabled={isVerifyingMfa || !verificationCode}>
                           {isVerifyingMfa
                               ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t('escort:security.verifying')}</>
                               : t('escort:security.verify_and_activate')}
                        </Button>
                         <Button variant="outline" onClick={() => { setEnrollmentFactorId(null); setEnrollmentQrCodeSvg(null); }}>
                            {t('common:cancel')}
                         </Button>
                     </div>
                 </div>
             )}

             {/* Si 2FA est activée (vérifié via isTotpEnabled()) */}
             {isTotpEnabled() && (
                 <div className="space-y-4">
                     <Alert variant="success"> { /* Shadcn ne semble pas avoir de variant success, on utilise default */} 
                         <Check className="h-4 w-4" />
                         <AlertDescription>
                            {t('escort:security.2fa_is_enabled')}
                         </AlertDescription>
                     </Alert>
                      {/* Utiliser handleDisableMfa pour désactiver */}
                     <Button variant="destructive" onClick={handleDisableMfa} disabled={isDisablingMfa}>
                         {isDisablingMfa
                            ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t('escort:security.disabling')}</>
                            : t('escort:security.disable_2fa_button')}
                     </Button>
                 </div>
             )}

            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="blocked">
          <Card>
            <CardHeader>
              <CardTitle>{t('escort:security.blocked_users')}</CardTitle>
              <CardDescription>
                {t('escort:security.blocked_users_description')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingBlockedUsers ? (
                 <div className="flex justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                 </div>
              ) : blockedUsers.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  {t('escort:security.no_blocked_users')}
                </p>
              ) : (
                <ul className="space-y-4">
                  {blockedUsers.map((blockedUser) => (
                    <li key={blockedUser.relationshipId} className="flex items-center justify-between border-b pb-4 last:border-b-0 last:pb-0">
                      <div className="flex items-center gap-3">
                        <Avatar>
                          <AvatarImage src={blockedUser.avatar || undefined} alt={blockedUser.name} />
                          <AvatarFallback>{blockedUser.name?.charAt(0)?.toUpperCase() || 'U'}</AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium">{blockedUser.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {t('escort:security.blocked_on')} {new Date(blockedUser.blockedDate).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <Button 
                        variant="outline"
                        size="sm"
                        onClick={() => handleUnblockUser(blockedUser.relationshipId)} // Utiliser l'ID de la relation
                      >
                        {t('escort:security.unblock')}
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="emergency">
          <Card>
            <CardHeader>
              <CardTitle>{t('escort:security.panic_button')}</CardTitle>
              <CardDescription>
                {t('escort:security.panic_button_description')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <Alert variant="destructive" className="flex flex-col items-center gap-4 p-6 text-center">
                <AlertTriangle className="h-10 w-10" />
                <AlertDescription>
                  {t('escort:security.panic_button_description')}
                </AlertDescription>
                <Button size="lg" className="bg-red-600 hover:bg-red-700">
                  <AlertTriangle className="h-5 w-5 mr-2" />
                  {t('escort:security.activate_panic_button')}
                  {/* TODO: onClick action for panic button */}
                </Button>
              </Alert>
              
              <div className="space-y-4">
                <h3 className="text-lg font-medium">{t('escort:security.your_contacts')}</h3>
                {isLoadingEmergencyContacts ? (
                    <div className="flex justify-center py-8">
                       <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                ) : emergencyContacts.length === 0 && !showAddContactForm ? (
                    <p className="text-muted-foreground text-center py-8">
                       {t('escort:security.no_emergency_contacts')}
                    </p>
                ) : (
                   <ul className="space-y-4">
                     {emergencyContacts.map((contact) => (
                       <li key={contact.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b pb-4 last:border-b-0 last:pb-0">
                         <div className="flex items-center gap-3">
                           <div className={`flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center ${contact.is_main ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                              <Phone className="h-5 w-5" />
                           </div>
                           <div>
                             <p className="font-medium flex items-center gap-2">
                                {contact.name}
                                {contact.is_main && <Badge>{t('escort:security.main')}</Badge>}
                             </p>
                             <p className="text-sm text-muted-foreground">{contact.phone}</p>
                             {contact.email && <p className="text-xs text-muted-foreground">{contact.email}</p>}
                           </div>
                         </div>
                         <div className="flex gap-2 justify-end flex-shrink-0">
                            {!contact.is_main && (
                                <Button 
                                  variant="outline"
                                  size="sm" 
                                  onClick={() => handleSetMainContact(contact.id)}
                                  disabled={isSettingMain === contact.id || !!isSettingMain}
                                >
                                   {isSettingMain === contact.id 
                                      ? <Loader2 className="h-4 w-4 animate-spin" /> 
                                      : t('escort:security.set_as_main')}
                                </Button>
                            )}
                           <Button 
                             variant="ghost" 
                             size="icon" 
                             onClick={() => handleDeleteContact(contact.id)}
                             disabled={isDeletingContact === contact.id}
                             className="text-destructive hover:text-destructive"
                            >
                              {isDeletingContact === contact.id 
                                ? <Loader2 className="h-4 w-4 animate-spin" /> 
                                : <Trash2 className="h-4 w-4" />}
                           </Button>
                         </div>
                       </li>
                     ))}
                   </ul>
                )}
              </div>

              {showAddContactForm ? (
                <Card className="bg-muted/50 p-6">
                  <h3 className="text-lg font-medium mb-4">{t('escort:security.add_new_contact')}</h3>
                  <div className="space-y-4">
                     <div className="space-y-1">
                       <Label htmlFor="contact-name">{t('escort:security.contact_name')} *</Label>
                       <Input id="contact-name" value={contactName} onChange={(e) => setContactName(e.target.value)} />
                     </div>
                      <div className="space-y-1">
                       <Label htmlFor="contact-phone">{t('escort:security.contact_phone')} *</Label>
                       <Input id="contact-phone" type="tel" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} />
                     </div>
                      <div className="space-y-1">
                       <Label htmlFor="contact-email">{t('escort:security.contact_email')}</Label>
                       <Input id="contact-email" type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
                     </div>
                     <div className="flex justify-end gap-2 pt-4">
                         <Button variant="outline" onClick={() => setShowAddContactForm(false)}>{t('common:cancel')}</Button>
                         <Button onClick={handleAddContact} disabled={isSavingContact}>
                             {isSavingContact 
                                ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t('common:saving')}</> 
                                : t('common:add')}
                         </Button>
                     </div>
                  </div>
                </Card>
              ) : (
                <div className="flex justify-center pt-4">
                   <Button variant="outline" onClick={() => setShowAddContactForm(true)}>
                     <Plus className="mr-2 h-4 w-4" /> {t('escort:security.add_contact_button')}
                   </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default EscortSecurity;
